import { useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { Seo, JsonLd } from "@/components/base/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, categories, type VerdictType } from "@/mocks/articles";

const verdicts: VerdictType[] = ["已证实", "已辟谣", "存疑待定", "出现反转", "打脸现场"];

export default function Verify() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const verdict = params.get("verdict") || "";
  const [draft, setDraft] = useState(q);

  const results = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return articles.filter((a) => {
      if (category && a.category !== category) return false;
      if (verdict && a.verdict !== verdict) return false;
      if (kw) {
        const haystack = `${a.title} ${a.summary} ${a.tags.join(" ")}`.toLowerCase();
        if (!haystack.includes(kw)) return false;
      }
      return true;
    });
  }, [q, category, verdict]);

  function update(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  function onSearch(e: FormEvent) {
    e.preventDefault();
    update("q", draft.trim());
  }

  function resetAll() {
    setDraft("");
    setParams(new URLSearchParams(), { replace: true });
  }

  const activeCount = (category ? 1 : 0) + (verdict ? 1 : 0) + (q ? 1 : 0);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "真假鉴定专区",
      url: "https://dahuaqi.cn/verify",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://dahuaqi.cn/" },
        { "@type": "ListItem", position: 2, name: "真假鉴定专区", item: "https://dahuaqi.cn/verify" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: results.map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: a.title,
        url: `https://dahuaqi.cn/article/${a.id}`,
      })),
    },
  ];

  return (
    <Layout>
      <Seo
        title="真假鉴定专区 - 全部辟谣与反转条目 | 17爆料网"
        description="17爆料网真假鉴定专区汇总全部条目，支持按主题分类与鉴定结论筛选，可搜索关键词，查看辟谣、反转与打脸现场的完整依据。"
        keywords="17爆料网真假,17爆料网辟谣,17爆料网反转,打脸现场,后续回应,鉴定专区"
        path="/verify"
      />
      <JsonLd data={jsonLd} />

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-12">
          <Breadcrumb items={[{ label: "真假鉴定专区" }]} />
          <h1 className="mt-5 font-heading text-3xl font-800 text-foreground-950 md:text-4xl">真假鉴定专区</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里收录 17爆料网的全部鉴定条目。可按主题分类与鉴定结论筛选，也可直接搜索关键词，查看每条传言的原始出处、当事人回应与最终结论。
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-14">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <form onSubmit={onSearch} className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-lg text-foreground-400"></i>
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                type="search"
                aria-label="搜索鉴定条目"
                placeholder="搜索标题、摘要或标签"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-col gap-3 border-t border-background-200 pt-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs font-500 text-foreground-500">主题</span>
              <button
                type="button"
                onClick={() => update("category", "")}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                  category ? "bg-background-100 text-foreground-600 hover:bg-background-200" : "bg-secondary-500 text-background-50"
                }`}
              >
                全部
              </button>
              {categories.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => update("category", category === c.key ? "" : c.key)}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                    category === c.key
                      ? "bg-secondary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {c.key}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs font-500 text-foreground-500">结论</span>
              <button
                type="button"
                onClick={() => update("verdict", "")}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                  verdict ? "bg-background-100 text-foreground-600 hover:bg-background-200" : "bg-accent-500 text-background-50"
                }`}
              >
                全部
              </button>
              {verdicts.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => update("verdict", verdict === v ? "" : v)}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                    verdict === v ? "bg-accent-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-600">
            共筛选出 <strong className="font-600 text-foreground-950">{results.length}</strong> 条鉴定记录
            {activeCount > 0 && <span className="ml-2 text-foreground-400">（已应用 {activeCount} 个条件）</span>}
          </p>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-primary-700 transition-colors hover:text-primary-800"
            >
              <i className="ri-refresh-line"></i>
              清除筛选
            </button>
          )}
        </div>

        {results.length > 0 ? (
          <div className="mt-5 flex flex-col gap-4">
            {results.map((a) => (
              <ArticleCard key={a.id} article={a} variant="list" />
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-file-search-line text-2xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-600 text-foreground-950">没有找到对应条目</h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              换个关键词试试，或清除筛选条件查看全部记录。
            </p>
            <button
              type="button"
              onClick={resetAll}
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
            >
              查看全部条目
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}